/**
 * Branch selection — persisted in localStorage so it survives reloads and is
 * readable from plain modules (api.js) that aren't React components.
 *
 * "all" is only a valid choice for report-style endpoints (dashboard,
 * transactions, transaction-history) — everything else (tables, orders,
 * stock, ...) needs exactly one real branch, since "create a table in every
 * branch at once" isn't a real action. getRequiredBranchHeaders() handles
 * that distinction.
 */

const STORAGE_KEY = 'selectedBranchId';
const FALLBACK_KEY = 'firstBranchId';
export const ALL_BRANCHES = 'all';

export function getSelectedBranchId() {
  return localStorage.getItem(STORAGE_KEY) || ALL_BRANCHES;
}

export function setSelectedBranchId(id) {
  localStorage.setItem(STORAGE_KEY, id);
}

// เก็บ "สาขาแรกในรายการ" ไว้ต่างหาก (ตั้งค่าโดย BranchContext ตอนโหลดรายชื่อสาขา)
// เพื่อให้ api.js (ไม่ใช่ React component เข้าถึง context ไม่ได้) มี fallback ใช้ได้
// เวลาผู้ใช้เลือก "รวมทุกสาขา" อยู่แต่กำลังเรียก endpoint ที่ต้องมีสาขาเดียว
export function cacheFallbackBranchId(id) {
  if (id) localStorage.setItem(FALLBACK_KEY, id);
}

export function getCachedFallbackBranchId() {
  return localStorage.getItem(FALLBACK_KEY) || null;
}

/** Report-style endpoints — "all" is a real, supported choice (HQ rollup). */
export function getReportBranchHeaders() {
  const id = getSelectedBranchId();
  return id === ALL_BRANCHES ? { 'X-Branch-Scope': 'all' } : { 'X-Branch-Id': id };
}

/**
 * Transactional endpoints — always exactly one branch. If the user currently
 * has "รวมทุกสาขา" selected, falls back to `fallbackBranchId` (normally the
 * first branch in the list, or the cached one when called from a plain
 * module that has no React context) since there's no such thing as "all
 * branches" for an action like opening a table.
 */
export function getRequiredBranchHeaders(fallbackBranchId) {
  const id = getSelectedBranchId();
  const effective = id === ALL_BRANCHES ? (fallbackBranchId ?? getCachedFallbackBranchId()) : id;
  return effective ? { 'X-Branch-Id': effective } : {};
}
